import SearchIcon from '@mui/icons-material/Search';
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { PropsWithChildren, useContext } from 'react';

import { AutocompleteGenericEntity } from '@australiangreens/ag-internal-components';
import { useLookupCatCampaigns } from '@components/CampaignAutocomplete';
import { AccordionField } from '@components/DrawerAccordion';
import UsersFetchAutocomplete from '@components/UsersFetchAutocomplete';
import MissingFilterValueWarning from '@pages/AdvancedSearchListCreatorPage/customWidgets/MissingFilterValueWarning';
import TemplateCapableFetchAutocomplete, {
  TemplateCapableFetchAutocompleteChangeReason,
} from '@/components/TemplateCapableFetchAutocomplete';
import { MappingError } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import {
  AutocompleteResult,
  AutocompleteResultWithTooltip,
  isTemplatePlaceholder,
  TemplatePlaceholder,
} from '@/types';
import CallingStatusDateRangePicker, {
  TemplatableCallingStatusDateRange,
} from './CallingStatusDateRangePicker';
import { ListRecordResult } from '@/types/filterCriteriaRecords';
import Lists from './Lists';

import { CALLING_STATUS_OPTIONS_IDS, mapCampaignsToTooltips } from '@/constants/filterCriteria/callingStatusOptions';

const MissingValueField = ({
  children,
  isAdvancedSearch,
  isMissingValue,
  isRequired,
}: PropsWithChildren<{
  isAdvancedSearch: boolean;
  isMissingValue: boolean;
  isRequired: boolean;
}>) => {
  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: 1,
          width: '100%',
        },
        isAdvancedSearch ? { width: '50ch' } : {},
      ]}
    >
      {isAdvancedSearch && (
        <MissingFilterValueWarning isMissing={isMissingValue} isRequired={isRequired} />
      )}
      <Box sx={{
        flexGrow: 1
      }}>{children}</Box>
    </Box>
  );
};

type CallingStatusFormProps = {
  onCallingStatusChange: (
    newValue: AutocompleteResult[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  callingStatusValue: AutocompleteResult[] | TemplatePlaceholder;
  callingStatusInclude: boolean;
  setCallingStatusInclude: (newValue: boolean) => void;
  onCallingStatusDateChange: (newValue: TemplatableCallingStatusDateRange) => void;
  callingStatusDateValue: TemplatableCallingStatusDateRange;
  setIsFilterInvalid: (isInvalid: boolean) => void;
  callingStatusLists: ListRecordResult[] | TemplatePlaceholder;
  onCallingStatusListsChange: (
    newValue: ListRecordResult[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  callingStatusUsers: AutocompleteGenericEntity[] | TemplatePlaceholder;
  onCallingStatusUsersChange: (
    newValue: AutocompleteGenericEntity[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  callingStatusCampaigns: AutocompleteResultWithTooltip[] | TemplatePlaceholder;
  onCallingStatusCampaignsChange: (
    newValue: AutocompleteResultWithTooltip[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  isAdvancedSearch?: boolean;
  errors?: MappingError[];
};

const CallingStatusForm = ({
  callingStatusValue = [],
  onCallingStatusChange = () => {},
  callingStatusInclude = false,
  setCallingStatusInclude = () => {},
  setIsFilterInvalid = () => {},
  onCallingStatusDateChange = () => {},
  callingStatusDateValue = {} as TemplatableCallingStatusDateRange,
  callingStatusLists = [] as ListRecordResult[],
  onCallingStatusListsChange = () => {},
  callingStatusUsers = [] as AutocompleteGenericEntity[],
  onCallingStatusUsersChange = () => {},
  callingStatusCampaigns = [] as AutocompleteResultWithTooltip[],
  onCallingStatusCampaignsChange = () => {},
  isAdvancedSearch = false,
  errors,
}: CallingStatusFormProps) => {
  const isOnTemplatePage = useContext(SearchTemplatePageContext);
  const toggleValue = callingStatusInclude ? 'include' : 'exclude';
  const lookupCampaigns = useLookupCatCampaigns();

  async function lookupCampaignToToolTips(lookupValue: string) {
    const campaigns = await lookupCampaigns(lookupValue);
    const tooltips = campaigns.map((x) => mapCampaignsToTooltips(x));
    return tooltips;
  }

  const isMissingCampaigns = !!errors?.find(({ field }) => field === 'campaigns');
  const isMissingUsers = !!errors?.find(({ field }) => field === 'users');
  const isMissingLists = !!errors?.find(({ field }) => field === 'lists');

  const showSecondaryFields =
    isTemplatePlaceholder(callingStatusValue) || callingStatusValue.length > 0;

  return (
    <>
      {!isAdvancedSearch && (
        <AccordionField title="">
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Include or exclude people with selected calling statuses"
            disabled={!isTemplatePlaceholder(callingStatusValue) && callingStatusValue.length === 0}
            value={toggleValue}
            onChange={(_e, value) => {
              if (value === 'include') {
                setCallingStatusInclude(true);
              }
              if (value === 'exclude') {
                setCallingStatusInclude(false);
              }
            }}
          >
            <ToggleButton sx={{ width: '140px' }} value="include">
              Include
            </ToggleButton>
            <ToggleButton sx={{ width: '140px' }} value="exclude">
              Exclude
            </ToggleButton>
          </ToggleButtonGroup>
        </AccordionField>
      )}

      <AccordionField title={isAdvancedSearch ? undefined : 'People called with status(es)'}>
        <TemplateCapableFetchAutocomplete
          sx={isAdvancedSearch ? { width: '50ch' } : {}}
          minLength={0}
          label="Add a calling status"
          preLoadedOptions={CALLING_STATUS_OPTIONS_IDS}
          value={callingStatusValue}
          onChange={onCallingStatusChange}
          loadingText="Looking for calling statuses..."
          noOptionsText="No calling statuses found"
          isOnTemplatePage={isOnTemplatePage}
          templateBuilderDisplayName="Calling status filter: Calling status"
        />
      </AccordionField>

      {!showSecondaryFields && (
        <AccordionField
          title="
        Select at least one status to see more options"
        />
      )}

      {showSecondaryFields && (
        <AccordionField>
          <Stack sx={{ gap: 2, width: '100%' }}>
            <CallingStatusDateRangePicker
              setIsFilterInvalid={setIsFilterInvalid}
              onChange={onCallingStatusDateChange}
              value={callingStatusDateValue}
              isAdvancedSearch={isAdvancedSearch}
            />
            <MissingValueField
              isMissingValue={isMissingLists}
              isAdvancedSearch={isAdvancedSearch}
              isRequired={false}
            >
              <Lists
                onChange={onCallingStatusListsChange}
                value={callingStatusLists}
                listLabel="Contacted in list(s) (optional)"
                error={isMissingLists}
              />
            </MissingValueField>
            <MissingValueField
              isMissingValue={isMissingUsers}
              isAdvancedSearch={isAdvancedSearch}
              isRequired={false}
            >
              <UsersFetchAutocomplete
                label={'Contacted by users(s) (optional)'}
                value={callingStatusUsers}
                onChange={onCallingStatusUsersChange}
                dataTestidPrefix={'contactingUsers'}
                error={isMissingUsers}
              />
            </MissingValueField>
            <MissingValueField
              isAdvancedSearch={isAdvancedSearch}
              isMissingValue={isMissingCampaigns}
              isRequired={false}
            >
              <TemplateCapableFetchAutocomplete
                lookup={lookupCampaignToToolTips}
                label="Contacted by campaign(s) (optional)"
                value={callingStatusCampaigns}
                onChange={onCallingStatusCampaignsChange}
                loadingText="Looking for campaigns..."
                noOptionsText="No campaigns found"
                popupIcon={<SearchIcon />}
                disableIconFlip
                error={isMissingCampaigns}
                isOnTemplatePage={isOnTemplatePage}
                templateBuilderDisplayName="Calling status filter: Campaign(s)"
              />
            </MissingValueField>
          </Stack>
        </AccordionField>
      )}
    </>
  );
};

export default CallingStatusForm;
